import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import type { CellComponentProps } from "../DataTable.types";

export interface CustomCellProps<T = any> extends CellComponentProps<T> {
  /**
   * Custom render function that returns the full cell content
   * Receives value, row, and context for maximum flexibility
   */
  render: (value: any, row: T, context: CellComponentProps<T>["context"]) => ReactNode;

  /**
   * Optional className to apply to the wrapper
   */
  className?: string;
}

/**
 * CustomCell provides complete control over cell rendering
 * with no default padding or styling applied.
 *
 * This cell is designed to be used with `noCellPadding: true` on the column
 * to give you full control over the cell's appearance, including background colors
 * that extend to the edges without padding interference.
 *
 * @example
 * ```tsx
 * // Full-background colored cell
 * {
 *   id: "status",
 *   accessor: "status",
 *   cell: CustomCell,
 *   noCellPadding: true,
 *   cellProps: {
 *     render: (value, row) => (
 *       <div className="h-full w-full bg-red-500 flex items-center justify-center px-4 py-3">
 *         {value}
 *       </div>
 *     )
 *   }
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Gradient background with custom content
 * {
 *   id: "score",
 *   accessor: "score",
 *   cell: CustomCell,
 *   noCellPadding: true,
 *   cellProps: {
 *     render: (value, row) => {
 *       const intensity = value / 100;
 *       return (
 *         <div
 *           className="h-full w-full flex items-center px-4 py-3"
 *           style={{
 *             background: `linear-gradient(to right, rgba(68, 74, 225, ${intensity}), transparent)`
 *           }}
 *         >
 *           <span className="font-semibold">{value}%</span>
 *         </div>
 *       );
 *     }
 *   }
 * }
 * ```
 */
export function CustomCell<T = any>({ value, row, context, render, className }: CustomCellProps<T>) {
  const content = render(value, row, context);

  return <div className={twMerge("h-full w-full", className)}>{content}</div>;
}
